import PageHeader from '../components/ui/PageHeader'
import FileDownloadIcon from '@offline-mui/icons-material/FileDownload'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TablePagination,
} from '@offline-mui/material'
import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useSearchParams } from '../router'
import { fetchBookings, fetchInventoryById } from '../api/inventory'
import type { InventoryBooking, InventoryItinerary } from '../api/inventory'
import BookingResultCard from '../components/BookingResultCard'
import EditBookingDialog from '../components/EditBookingDialog'
import {
  bookingsCsvFilename,
  downloadBookingsCsv,
} from '../utils/bookingCsv'
import { fareTotal, formatMoney } from '../utils/flightFormat'

export default function BookingsPage() {
  const [searchParams] = useSearchParams()
  const itineraryIdParam = searchParams.get('itineraryId')
  const itineraryId = itineraryIdParam ? Number(itineraryIdParam) : null

  const [rows, setRows] = useState<InventoryBooking[]>([])
  const [itinerary, setItinerary] = useState<InventoryItinerary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [exportMessage, setExportMessage] = useState<string | null>(null)
  const [editing, setEditing] = useState<InventoryBooking | null>(null)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setPage(0)
    try {
      const [bookingResponse, itineraryResponse] = await Promise.all([
        fetchBookings(itineraryId ? { itineraryId } : {}),
        itineraryId ? fetchInventoryById(itineraryId).catch(() => null) : Promise.resolve(null),
      ])
      setRows(bookingResponse.data)
      setItinerary(itineraryResponse?.data || null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings')
      setRows([])
      setItinerary(null)
    } finally {
      setLoading(false)
    }
  }, [itineraryId])

  useEffect(() => {
    void load()
  }, [load])

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  const itineraryPrice = itinerary ? fareTotal(itinerary.fares?.[0]) : null

  const handleExportCsv = () => {
    downloadBookingsCsv(rows, bookingsCsvFilename(itineraryId))
    setExportMessage(`Exported ${rows.length} bookings to CSV`)
    window.setTimeout(() => setExportMessage(null), 2000)
  }

  const handleSaved = (booking: InventoryBooking) => {
    setRows((prev) =>
      prev.map((row) => (row.booking_id === booking.booking_id ? booking : row)),
    )
    setSaveMessage(`Updated PNR and e-ticket for ${booking.booking_id}`)
    window.setTimeout(() => setSaveMessage(null), 2000)
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        eyebrow="Inventory"
        title="Bookings"
        description={
          itineraryId
            ? `Bookings created from block #${itineraryId}.`
            : 'All bookings across blocks, including passenger and payment details.'
        }
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label={`${rows.length} bookings`} size="small" />
          {itineraryId ? (
            <Chip
              component={RouterLink}
              to="/offline-flight/bookings"
              clickable
              label="Show all bookings"
              size="small"
              variant="outlined"
            />
          ) : null}
          {itinerary ? (
            <Chip
              label={`${itinerary.plating_carrier} · ${itinerary.fare_type} · ${formatMoney(itineraryPrice)}`}
              size="small"
              variant="outlined"
            />
          ) : null}
        </Stack>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCsv}
          disabled={!rows.length || loading}
        >
          Export CSV
        </Button>
      </Stack>

      {exportMessage && <Alert severity="success">{exportMessage}</Alert>}
      {saveMessage && <Alert severity="success">{saveMessage}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          {itineraryId
            ? `No bookings found for block #${itineraryId}.`
            : 'No bookings found.'}
        </Alert>
      ) : (
        <>
          <Paper elevation={1} sx={{ overflow: 'hidden' }}>
            {pagedRows.map((booking) => (
              <BookingResultCard
                key={booking.booking_id}
                booking={booking}
                onEdit={setEditing}
              />
            ))}
          </Paper>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </>
      )}

      <EditBookingDialog
        open={Boolean(editing)}
        booking={editing}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />
    </Stack>
  )
}
